/** The visit popover (moved from `quote/quote-editors.tsx` at step 8). */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  VISIT_ROLE_LABELS, followersOf, proposalLineOf, resolveVisitDates, sameWhen, visitWhenPhrase,
  visitWithWhen,
  type QuoteBillingPlanView, type QuoteTree, type QuoteVisitView, type VisitRole, type VisitWhen,
} from '@rowted/shared'
import { FieldShell } from '../../components/fields.js'
import {
  EditorActions, EditorHeader, EditorInput, EditorShell, FooterTotal,
} from '../editor-shell.js'
import { formatRate } from '../../lib/format.js'
import { usePhone } from '../../lib/use-narrow.js'
import { OptionsBlock } from '../fields/options-block.js'
import { AUTO_NAMED, IN_A_CHOICE, KIND_OF_VISIT, VISIT_TOTAL } from '../copy.js'
import { ChoicesField } from '../fields/choices-field.js'
import { KindField } from '../fields/kind-field.js'
import { VisitWhenBlock, useResolvedDraft } from '../fields/visit-when-block.js'
import { BillingField } from '../fields/billing-field.js'
import { NotesField } from '../fields/notes-field.js'
import { MovePrompt, type MoveFollower } from './move-prompt.js'

// ---------------------------------------------------------------------------
// §5.3 — the visit editor
// ---------------------------------------------------------------------------

/**
 * One visit, on the same anatomy as the line popup.
 *
 * It used to be a column of eleven fields under a header that said "Visit",
 * with Cancel and Done in the footer and nothing else — so you could not tell
 * from the box which visit you had opened, or what changing the schedule had
 * done to the number of trips, without closing it. Now:
 *
 *   header  a calendar tile, the visit's name, and what it is — "Work ·
 *           Weekly · 30 visits · 1 service"
 *   body    Name + Kind of visit, then **When** (visits §4.5, Board 8),
 *           Billing, Notes, Choices and Options, in Board 7's order
 *   footer  what this visit is worth, then Remove and Done
 */
/** Two id lists hold the same ids, whatever the order. */
function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

export function VisitPopover({
  visit, plans, options, groups = [], people, anchor, section, onClose, onSave, onRemove,
  onCreatePlan, onCreateChoice, onRenameQuestion, when, quote, onSaveWhen,
  inPackage = false, hasRental = false, totalLabel = VISIT_TOTAL, onOpenBilling,
}: {
  visit: QuoteVisitView
  /**
   * The When field, where a page's visits do not start on a calendar date
   * (`BuilderAdapter.starts: 'relative'`). Given, it is drawn in place of the
   * When block, and the patch carries no schedule — the page that drew it
   * owns what it says.
   */
  when?: ReactNode
  /**
   * The quote this visit is on: its visits, for *Which visit* and for
   * resolving the draft's dates, and its timing mode, for the Reads line's
   * **On the proposal** (visits §4.5).
   */
  quote?: Pick<QuoteTree, 'visits' | 'proposalTiming' | 'proposalDatesAllowed'>
  /**
   * Writes the When through the one writer (`PUT …/visits/:id/when`). Called
   * on Done only when the When changed — a Done with no change writes nothing
   * (§4.7).
   */
  onSaveWhen?: (when: VisitWhen, followers: 'move' | 'pin') => Promise<boolean>
  /** A package's visit (§10.1): the When block's package mode — no dates, *At the start*. */
  inPackage?: boolean
  /** Whether a rental's end is something this visit can follow. */
  hasRental?: boolean
  /** The footer's word for the visit's money — *At the sample measure* on a package. */
  totalLabel?: string
  plans: QuoteBillingPlanView[]
  /**
   * The answers already on this quote, for the **Choices** field (folder 44 §8).
   *
   * They come down on the tree rather than from their own request, so the
   * field, the Plans grid and the row's tag cannot disagree about which plans
   * exist while one of them is still loading.
   */
  options?: QuoteTree['chooseOptions']
  /** The questions those answer, for the Choices field's headings and helper. */
  groups?: QuoteTree['chooseGroups']
  /** Writes a choice now (§8.4) and answers with it, so the field can tick it. */
  onCreateChoice?: (name: string, groupId?: string, newQuestion?: boolean)
    => Promise<{ id: string; groupId: string } | null>
  /** Reword the question the customer is asked. */
  onRenameQuestion?: (groupId: string, name: string) => Promise<boolean>
  /**
   * Make a plan from the dropdown and put this visit on it.
   *
   * Without it the only way to get a plan was the Billing tab, which meant
   * leaving a half-filled editor to go and make one.
   */
  onCreatePlan?: (name: string) => Promise<void>
  /** cb-07's `Open the Billing tab ›`: closes the popover and opens the tab. */
  onOpenBilling?: () => void
  people: { id: string; name: string }[]
  anchor: HTMLElement | null
  /** Which part to scroll to — the card's three facts each open their own. */
  section?: 'name' | 'schedule' | 'billing' | 'notes' | 'choices'
  onClose: () => void
  onSave: (patch: Record<string, unknown>) => Promise<boolean>
  /** Take the visit off the quote — the line popup's Remove, one level up. */
  onRemove?: () => void
}) {
  const [name, setName] = useState(visit.autoNamed ? '' : visit.name)
  const [role, setRole] = useState<VisitRole>(visit.role)
  // Board 17: on a phone Name and Kind of visit stack rather than share a row.
  const phone = usePhone()
  const [whenDraft, setWhenDraft] = useState<VisitWhen>(visit.when)
  const [planId, setPlanId] = useState(visit.billingPlanId)
  const [, setChoicesOpen] = useState(false)
  /*
   * **A plan made from the field is the one the field shows** (Aaron, step
   * 21 item 5). Creating one writes the plan *and* moves this visit onto it
   * in a single request, so the visit comes back on the new plan — and the
   * field went on showing the old one, which Done would then have written
   * back, quietly undoing the move. Following the visit's own plan when it
   * changes underneath keeps the popover open on the plan just made.
   */
  const seenPlan = useRef(visit.billingPlanId)
  useEffect(() => {
    if (visit.billingPlanId === seenPlan.current) return
    seenPlan.current = visit.billingPlanId
    setPlanId(visit.billingPlanId)
  }, [visit.billingPlanId])
  const [optional, setOptional] = useState(visit.optional)
  /*
   * **The whole set, never a delta** (§8.4). Ticks live here until Done; a
   * choice created in the field is already written, so only the membership
   * waits.
   */
  const initialChoices = useMemo(() => (options ?? [])
    .filter((one) => !one.declined && one.visitIds.includes(visit.id))
    .map((one) => one.id), [options, visit.id])
  const [choiceIds, setChoiceIds] = useState<string[]>(initialChoices)
  const choicesRef = useRef<HTMLDivElement>(null)
  const scheduleRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (section !== 'schedule') return undefined
    const frame = window.requestAnimationFrame(() => {
      scheduleRef.current?.scrollIntoView({ block: 'nearest' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [section])
  useEffect(() => {
    if (section !== 'choices') return undefined
    /* A frame later: the popover is placed (and visible) after its children mount. */
    const frame = window.requestAnimationFrame(() => {
      choicesRef.current?.scrollIntoView({ block: 'center' })
      choicesRef.current?.querySelector<HTMLElement>('[data-chips-field]')?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [section])
  const [noteQuote, setNoteQuote] = useState(visit.noteQuote)
  const [noteInternal, setNoteInternal] = useState(visit.noteInternal)
  const [noteCrew, setNoteCrew] = useState(visit.noteCrew)
  const [busy, setBusy] = useState(false)

  /*
   * The draft resolved over the quote's other visits — the count, the dates
   * and the words Done will store, so the header, the Reads line and the ×N
   * the Choices field shows all say the same thing before anything is saved.
   */
  const siblings = useMemo(() => (quote?.visits ?? [visit])
    .map((one) => ({ id: one.id, name: one.name, when: one.when })), [quote?.visits, visit])
  const { resolved, names } = useResolvedDraft({ when: whenDraft, selfId: visit.id, visits: siblings })
  const draftPhrase = visitWhenPhrase(whenDraft, { names, resolved })
  const makes = resolved.occurrences ?? Number.POSITIVE_INFINITY

  /*
   * **Moving a visit that others follow** (§5.3). Done holds while the
   * prompt is up; the answer says how the When is written.
   */
  const [prompt, setPrompt] = useState<{ to: string; followers: MoveFollower[] } | null>(null)
  const promptAnchor = useRef<HTMLElement | null>(null)

  /** What would move if this draft were saved — or null when nothing follows, or the day did not move. */
  function moveFor(): { to: string; followers: MoveFollower[] } | null {
    const following = followersOf(siblings, visit.id)
    if (following.length === 0) return null
    const before = resolveVisitDates(siblings, { rentalEnd: null })
    const after = resolveVisitDates(
      siblings.map((one) => (one.id === visit.id ? { ...one, when: whenDraft } : one)), { rentalEnd: null })
    const from = before.get(visit.id)?.firstOn ?? null
    const to = after.get(visit.id)?.firstOn ?? null
    if (!to || to === from) return null
    return {
      to,
      followers: following.map((id) => ({
        id,
        name: siblings.find((one) => one.id === id)?.name ?? '',
        from: before.get(id)?.firstOn ?? null,
        to: after.get(id)?.firstOn ?? null,
      })),
    }
  }

  async function done(followers?: 'move' | 'pin') {
    const whenChanged = when === undefined && Boolean(onSaveWhen) && !sameWhen(whenDraft, visit.when)
    if (whenChanged && !followers) {
      const move = moveFor()
      if (move) {
        promptAnchor.current = document.activeElement as HTMLElement | null
        setPrompt(move)
        return
      }
    }
    setPrompt(null)
    setBusy(true)
    try {
      const ok = await onSave({
        name: name.trim(),
        // The kind only when it moved, so a visit of a kind now off saves untouched (§4.5.1).
        ...(role === visit.role ? {} : { role }),
        billingPlanId: planId,
        optional,
        /*
         * Choices (§8.4): the question follows the ticks on the server, and
         * an empty set makes the visit plain again. Sent only when it moved,
         * so a visit edited for its name keeps whatever it answers.
         */
        ...(sameSet(choiceIds, initialChoices) ? {} : {
          optionIds: choiceIds,
          /*
           * Unticking every choice leaves the visit **in its box**, in no
           * choice (step 22 item 2.2) — the same as the box's own grid. The
           * row's ⋮ is how a visit leaves. A package has no box to keep.
           */
          ...(!inPackage && choiceIds.length === 0 && visit.chooseGroupId
            ? { chooseGroupId: visit.chooseGroupId }
            : {}),
        }),
        noteQuote,
        noteInternal,
        noteCrew,
      })
      /*
       * The When last, through its own writer, and only when it changed: the
       * visit's other fields are already saved, so a refused When (a cycle)
       * leaves the popover open on the one thing that needs fixing.
       */
      const whenOk = ok && whenChanged && onSaveWhen
        ? await onSaveWhen(whenDraft, followers ?? 'move')
        : ok
      if (whenOk) onClose()
    } finally {
      setBusy(false)
    }
  }

  /*
   * `{Kind} · {short} · {n} service(s)` (§4.4): "Work · Weekly · 30 visits ·
   * 1 service", from the draft, so changing Repeats changes the header as
   * well as the Reads line.
   */
  const services = visit.services.length
  const facts = [
    VISIT_ROLE_LABELS[role],
    when === undefined ? draftPhrase.short : null,
    `${services} service${services === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ')

  /* What the page will print for the draft, in the quote's own timing mode (§9.1). */
  const proposalLine = quote
    ? () => proposalLineOf(visitWithWhen(visit, whenDraft, resolved),
      { mode: visit.proposal.timing ?? quote.proposalTiming, datesAllowed: quote.proposalDatesAllowed },
      { nameOf: (id) => names.get(id) ?? null })
    : undefined

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={visit.name || 'Visit'}
      header={(
        <EditorHeader kind="visit" name={visit.name || 'This visit'} facts={facts} />
      )}
      footer={(
        <EditorActions
          fact={<FooterTotal label={totalLabel} amount={formatRate(visit.total)} />}
          onRemove={onRemove ? () => { onRemove(); onClose() } : undefined}
          onDone={() => void done()}
          busy={busy}
        />
      )}
    >
      <div className="flex flex-col gap-4">
        {/* Name + Kind of visit (Board 8): one row, 1.4 : 1. */}
        <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-[1.4fr_1fr] gap-x-3.5 gap-y-3'}>
          <FieldShell label="Name">
            <span className="relative block">
              <EditorInput
                autoFocus={section === 'name'}
                value={name}
                aria-label="Visit name"
                placeholder={visit.autoNamed ? visit.name : 'Name this visit'}
                onChange={(event) => setName(event.target.value)}
                className={visit.autoNamed && !name ? 'pr-[84px] placeholder:text-text' : undefined}
              />
              {visit.autoNamed && !name && (
                <span className="pointer-events-none absolute right-[11px] top-1/2 -translate-y-1/2 text-[11px] text-text-faint">
                  {AUTO_NAMED}
                </span>
              )}
            </span>
          </FieldShell>
          <FieldShell label={KIND_OF_VISIT}>
            <KindField value={role} current={visit.role} onChange={setRole} />
          </FieldShell>
        </div>

        {when ?? (
          <div ref={scheduleRef}>
            <VisitWhenBlock
              when={whenDraft}
              onChange={setWhenDraft}
              selfId={visit.id}
              visits={siblings}
              /*
               * No quote line holds a rental term yet (step 13 DEVIATION
               * REQUEST 1), so *When the rental ends* is offered only to a
               * visit that already says it.
               */
              hasRental={hasRental}
              proposalLine={proposalLine}
              inPackage={inPackage}
            />
          </div>
        )}

        <BillingField plans={plans} value={planId} onChange={setPlanId} onCreatePlan={onCreatePlan}
          onOpenBilling={onOpenBilling} />

        <NotesField
          quote={noteQuote}
          internal={noteInternal}
          crew={noteCrew}
          onQuote={setNoteQuote}
          onInternal={setNoteInternal}
          onCrew={setNoteCrew}
        />

        {/*
          * **Choices** (§8, qb-05): after the notes, before Options, where
          * Board 7 draws it (build-both-together §3 row 4). Optional and a
          * choice are two ways for the customer to decide, so each goes quiet
          * while the other is on and says why.
          */}
        <div ref={choicesRef} className="border-t border-line-soft pt-3.5">
          <ChoicesField
            visitName={visit.name || 'This visit'}
            trips={Number.isFinite(makes) ? makes : null}
            groups={groups}
            options={options ?? []}
            value={choiceIds}
            onChange={setChoiceIds}
            onCreate={onCreateChoice ?? (async () => null)}
            onRename={onRenameQuestion ?? (async () => false)}
            optional={optional}
            wasInChoice={initialChoices.length > 0}
            boxName={!inPackage && visit.chooseGroupId
              ? groups.find((group) => group.id === visit.chooseGroupId)?.name ?? null
              : null}
            readOnly={visit.declined}
            /*
             * The open list sits in the popover's flow (qb-05 · 2–3), and
             * `Popover` re-measures when its children re-render — so the
             * state change is what lets it grow from 420 to 590 rather than
             * scroll.
             */
            onListOpenChange={setChoicesOpen}
          />
        </div>

        {people.length === 0 && <span className="hidden" />}

        {/*
          * Always last (§4.3). A visit in a choose-one group cannot also be
          * optional — they are two ways for the customer to decide — so the
          * box greys while a group is named rather than refusing on save.
          */}
        <OptionsBlock
          level="visit"
          optional={{
            checked: optional,
            onChange: setOptional,
            disabled: choiceIds.length > 0,
            disabledReason: IN_A_CHOICE,
          }}
        />
      </div>
      <MovePrompt
        open={prompt !== null}
        anchor={promptAnchor.current}
        name={visit.name || 'This visit'}
        to={prompt?.to ?? ''}
        followers={prompt?.followers ?? []}
        onMoveAll={() => void done('move')}
        onOnlyThis={() => void done('pin')}
        onCancel={() => { setPrompt(null); setWhenDraft(visit.when) }}
      />
    </EditorShell>
  )
}
